import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, AlertTriangle, Archive, RotateCcw } from "lucide-react";

/**
 * Public preview of the admin Supply Tier CRUD layout — read-only.
 * Reads from the PUBLIC /api/supply/tiers endpoint (which shows active
 * tiers only) so the owner can see the design without logging in.
 * Route: /preview/admin-supply-tiers
 */

const API = process.env.REACT_APP_BACKEND_URL;
const OXBLOOD = "#4A0E17";

export default function PreviewAdminSupplyTiers() {
  const [tiers, setTiers] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/supply/tiers`).then(({ data }) => setTiers(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-bone">
      <div className="max-w-[1000px] mx-auto px-6 lg:px-8 py-10">
        <p className="inline-block rounded-sm px-2 py-1 font-mono uppercase tracking-[0.22em] text-[10px] mb-4" style={{ background: `${OXBLOOD}12`, color: OXBLOOD }}>Read-only preview · admin auth stripped</p>
        <h1 className="font-sans font-bold text-[36px] leading-none tracking-[-0.01em] mb-2" style={{ color: OXBLOOD, fontFamily: "Manrope, ui-sans-serif, system-ui" }}>Supply Pricing Tiers</h1>
        <p className="text-[14px] mb-8" style={{ color: OXBLOOD, opacity: 0.7 }}>
          Fluid ladder of price tiers for the Supply catalog. Each Supply product gets assigned to one tier; every product in that tier is priced identically. Add as many as you need.
        </p>

        <div className="rounded-lg border p-4 mb-6 bg-white" style={{ borderColor: `${OXBLOOD}22` }}>
          <p className="font-mono uppercase tracking-[0.22em] text-[10.5px] mb-3" style={{ color: OXBLOOD, opacity: 0.7 }}>Add a tier</p>
          <div className="flex flex-col md:flex-row gap-2">
            <input disabled placeholder="Label (e.g. $65 Tier)" className="flex-1 border rounded-sm px-3 py-2 text-[13px]" style={{ borderColor: `${OXBLOOD}30`, color: OXBLOOD }} />
            <input disabled type="number" placeholder="Price ($)" className="w-32 border rounded-sm px-3 py-2 text-[13px]" style={{ borderColor: `${OXBLOOD}30`, color: OXBLOOD }} />
            <button disabled className="rounded-sm px-4 py-2 text-white font-mono uppercase tracking-[0.22em] text-[11px] inline-flex items-center gap-1.5 opacity-60" style={{ background: OXBLOOD }}>
              <Plus size={13} /> Add tier
            </button>
          </div>
        </div>

        <div className="rounded-lg border bg-white overflow-hidden" style={{ borderColor: `${OXBLOOD}22` }}>
          <table className="w-full">
            <thead>
              <tr style={{ background: `${OXBLOOD}08` }}>
                <th className="text-left px-4 py-3 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.65 }}>Label</th>
                <th className="text-right px-4 py-3 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.65 }}>Price</th>
                <th className="text-center px-4 py-3 font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.65 }}>Active</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {tiers.length === 0 && (
                <tr><td colSpan={4} className="text-center px-4 py-6 text-[13px] italic" style={{ color: OXBLOOD, opacity: 0.55 }}>No active tiers yet.</td></tr>
              )}
              {tiers.map((t) => (
                <tr key={t.id} className="border-t" style={{ borderColor: `${OXBLOOD}15` }}>
                  <td className="px-4 py-3"><input readOnly value={t.label} className="w-full border rounded-sm px-2 py-1 text-[13px] bg-white/60" style={{ borderColor: `${OXBLOOD}20`, color: OXBLOOD }} /></td>
                  <td className="px-4 py-3 text-right"><input readOnly value={t.price} className="w-24 border rounded-sm px-2 py-1 text-[13px] font-mono text-right bg-white/60" style={{ borderColor: `${OXBLOOD}20`, color: OXBLOOD }} /></td>
                  <td className="px-4 py-3 text-center">
                    <span className="relative inline-flex h-5 w-9 items-center rounded-full" style={{ background: t.active ? OXBLOOD : "#ccc" }}>
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white ${t.active ? "translate-x-5" : "translate-x-1"}`} />
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="rounded-sm border p-1.5 inline-flex opacity-50" style={{ borderColor: `${OXBLOOD}40`, color: OXBLOOD }}>
                      <Trash2 size={13} />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Danger zone preview (Task 3) */}
        <div className="mt-10 rounded-lg border p-5 bg-white" style={{ borderColor: `${OXBLOOD}55` }}>
          <div className="flex items-start gap-2 mb-3">
            <AlertTriangle size={18} style={{ color: OXBLOOD }} className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-mono uppercase tracking-[0.22em] text-[11px] font-bold" style={{ color: OXBLOOD }}>Migration · Danger zone</p>
              <p className="text-[13px] mt-1" style={{ color: OXBLOOD, opacity: 0.85 }}>
                Bulk-archive every Supply product before flipping to the new fixed-tier catalog. Archived rows disappear from the public shelf but stay in the database — order history keeps resolving names/SKUs. Restore anytime.
              </p>
              <div className="flex gap-4 mt-2 font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: OXBLOOD, opacity: 0.75 }}>
                <span>Live: <span className="tabular-nums">96</span></span>
                <span>Archived: <span className="tabular-nums">0</span></span>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <button disabled className="rounded-sm px-4 py-2 text-white font-mono uppercase tracking-[0.22em] text-[11px] inline-flex items-center gap-1.5 opacity-60" style={{ background: OXBLOOD }}>
              <Archive size={13} /> Archive all 96 live Supply products
            </button>
            <button disabled className="rounded-sm px-4 py-2 border font-mono uppercase tracking-[0.22em] text-[11px] inline-flex items-center gap-1.5 opacity-60 bg-white" style={{ borderColor: OXBLOOD, color: OXBLOOD }}>
              <RotateCcw size={13} /> Restore all 0 archived
            </button>
          </div>
          <p className="text-[11px] font-mono uppercase tracking-[0.22em] mt-3" style={{ color: OXBLOOD, opacity: 0.55 }}>
            Preview only — buttons wired in live admin only. Uses POST /api/admin/products/archive-tier with confirm token "ARCHIVE".
          </p>
        </div>
      </div>
    </div>
  );
}
